import * as React from 'react';
import { useState } from 'react';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Footer from '../informational/Footer';
import featuresConfig from './features.json';
import RouteIcon from '@mui/icons-material/Route';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import EditIcon from '@mui/icons-material/Edit';
import LinkIcon from '@mui/icons-material/Link';
import PrintIcon from '@mui/icons-material/Print';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import AutorenewIcon from '@mui/icons-material/Autorenew';

function DesktopHomeContent({ onLogin }) {
    const [features] = useState(featuresConfig.features);
    const [hoveredFeature, setHoveredFeature] = useState(null);
    const [isLoading] = useState(false);

    const handleFeatureHover = (feature) => {
        setHoveredFeature(feature);
    };

    const handleFeatureLeave = () => {
        // Keep the last hovered feature visible
    };

    const getIconComponent = (iconName) => {
        const iconMap = {
            Route: RouteIcon,
            ShowChart: ShowChartIcon,
            Edit: EditIcon,
            Link: LinkIcon,
            Print: PrintIcon,
            SaveAlt: SaveAltIcon,
            AutoRenew: AutorenewIcon,
        };
        const IconComponent = iconMap[iconName] || null;
        return IconComponent ? <IconComponent sx={{ fontSize: '2rem' }} /> : null;
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            background: 'linear-gradient( rgba(1, 2, 3, 0.3)), url("/landing/hero.jpg")',
            backgroundSize: 'cover',
            backgroundPosition: 'center top',
            backgroundAttachment: 'fixed',
            position: 'relative'
        }}>
            {/* Hero Section - Value Statement */}
            <Box sx={{
                position: 'relative',
                width: '100%',
                height: '60vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2
            }}>
                <Box
                    component="img"
                    src="/landing/chart.svg"
                    alt="Chart Overlay"
                    sx={{
                        position: 'absolute',
                        top: '40%',
                        left: '50%',
                        transform: 'translate(-50%, -40%)',
                        width: '65%',
                        height: 'auto',
                        opacity: 0.8,
                        zIndex: 1
                    }}
                />
                <Box sx={{
                    position: 'relative',
                    zIndex: 2,
                    textAlign: 'center',
                    // backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    padding: '2rem 4rem',
                    borderRadius: 2
                }}>
                    <Typography variant="h2" component="h1" sx={{
                        fontSize: { xs: '2.5rem', md: '3.5rem' },
                        fontWeight: 700,
                        color: 'white',
                        textShadow: '0 2px 4px rgba(0,0,0,0.5)'
                    }}>
                        Seamlessly create, customize,
                        <br />
                        and download elevation
                        <br />
                        cue cards for your adventures
                    </Typography>
                </Box>

                {/* Partnership statement positioned at 1/3 screen width */}
                <Box sx={{
                    position: 'absolute',
                    top: 80, // Position below navigation header (64px + 16px margin)
                    right: '5vw', // Use right positioning for better responsive behavior
                    zIndex: 2,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    padding: '12px 12px',
                    borderRadius: 2,
                    backdropFilter: 'blur(1px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ opacity: 0.7, color: 'white' }}>
                            In Partnership With
                        </Typography>
                        <Box
                            component="a"
                            href="https://acier.cc"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                color: 'white',
                                textDecoration: 'none',
                                '&:hover': { opacity: 0.8 },
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            <Box
                                component="img"
                                src="/acier_logo_full.jpg"
                                alt="Acier"
                                sx={{
                                    height: 40,
                                    width: 'auto'
                                }}
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Sections with Gradient Background */}
            <Box sx={{
                background: 'linear-gradient(to bottom, transparent 0%, rgba(45, 74, 105, 0.8) 10%, rgba(45, 74, 105, 0.9) 40%, #2d4a69 100%)',
                position: 'relative',
                zIndex: 2
            }}>
                {/* Action Buttons Section */}
                <Box sx={{
                    position: 'relative',
                    zIndex: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 48,
                    py: 8,
                    px: 4,
                    mt: 9
                }}>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={() => {
                            document.getElementById('get-connected-section')?.scrollIntoView({
                                behavior: 'smooth'
                            });
                        }}
                        sx={{
                            backgroundColor: '#FF5722',
                            color: 'white',
                            fontWeight: 600,
                            px: 4,
                            py: 1.5,
                            borderRadius: 2,
                            fontSize: '1.1rem',
                            '&:hover': {
                                backgroundColor: '#FF5722'
                            }
                        }}
                    >
                        Get Connected
                    </Button>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={() => {
                            document.getElementById('see-it-in-action-section')?.scrollIntoView({
                                behavior: 'smooth'
                            });
                        }}
                        sx={{
                            backgroundColor: '#1976d2',
                            color: 'white',
                            fontWeight: 600,
                            px: 4,
                            py: 1.5,
                            borderRadius: 2,
                            fontSize: '1.1rem',
                            '&:hover': {
                                backgroundColor: '#1565c0'
                            }
                        }}
                    >
                        See It in Action
                    </Button>
                </Box>

                {/* Process Flow Section */}
                <Box
                    id="get-connected-section"
                    sx={{
                        py: 8,
                        px: 4,
                        position: 'relative',
                        zIndex: 2
                    }}
                >
                    <Container maxWidth="lg">
                        <Typography variant="h3" component="h2" sx={{
                            textAlign: 'center',
                            mb: 6,
                            color: 'white',
                            fontWeight: 700
                        }}>
                            Get Connected
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'nowrap' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Typography variant="h5" sx={{
                                    color: 'white',
                                    fontWeight: 600,
                                    mb: 2,
                                    textAlign: 'center'
                                }}>
                                    Connect
                                </Typography>
                                <Box sx={{
                                    backgroundColor: 'white',
                                    borderRadius: 3,
                                    border: '1px solid #e9ecef',
                                    overflow: 'hidden',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                }}>
                                    <Box
                                        component="img"
                                        src="/connect.png"
                                        alt="Connect"
                                        sx={{
                                            width: '100%',
                                            height: 'auto',
                                            display: 'block'
                                        }}
                                    />
                                </Box>
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flex: 1,
                                minWidth: '200px',
                                height: '200px'
                            }}>
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="-20 0 200 200" width="100%" height="200" preserveAspectRatio="none">
                                    <path d="M -11 5 q 9 21 59 7 q 50 -9 63 39 Q 119 115 179 107 m -9 4 l 9 -4 l -7 -4" stroke="white"
                                        strokeWidth="4"
                                        fill="none"
                                        strokeDasharray="12,10"
                                        strokeLinecap="round"
                                        filter="drop-shadow(0 0 8px rgba(255,255,255,0.3))" />
                                </svg>

                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Typography variant="h5" sx={{
                                    color: 'white',
                                    fontWeight: 600,
                                    mb: 2,
                                    textAlign: 'center'
                                }}>
                                    Select
                                </Typography>
                                <Box sx={{
                                    backgroundColor: 'white',
                                    borderRadius: 3,
                                    border: '1px solid #e9ecef',
                                    overflow: 'hidden',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                }}>
                                    <Box
                                        component="img"
                                        src="/select.png"
                                        alt="Select"
                                        sx={{
                                            width: '100%',
                                            height: 'auto',
                                            display: 'block'
                                        }}
                                    />
                                </Box>
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flex: 1,
                                minWidth: '200px',
                                height: '200px',
                                transform: 'scaleY(-1)'
                            }}>
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="-20 0 200 200" width="100%" height="200" preserveAspectRatio="none">
                                    <path d="M -11 5 q 9 21 59 7 q 50 -9 63 39 Q 119 115 179 107 m -9 4 l 9 -4 l -7 -4" stroke="white"
                                        strokeWidth="4"
                                        fill="none"
                                        strokeDasharray="12,10"
                                        strokeLinecap="round"
                                        filter="drop-shadow(0 0 8px rgba(255,255,255,0.3))" />
                                </svg>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Typography variant="h5" sx={{
                                    color: 'white',
                                    fontWeight: 600,
                                    mb: 2,
                                    textAlign: 'center'
                                }}>
                                    Save
                                </Typography>
                                <Box sx={{
                                    backgroundColor: 'white',
                                    borderRadius: 3,
                                    border: '1px solid #e9ecef',
                                    overflow: 'hidden',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                }}>
                                    <Box
                                        component="img"
                                        src="/save.png"
                                        alt="Save"
                                        sx={{
                                            width: '100%',
                                            height: 'auto',
                                            display: 'block'
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Container>
                </Box>

                {/* Features Section */}
                <Box
                    id="see-it-in-action-section"
                    sx={{
                        py: 8,
                        px: 4,
                        position: 'relative',
                        zIndex: 2
                    }}
                >
                    <Container maxWidth="lg">
                        <Typography variant="h3" component="h2" sx={{
                            textAlign: 'center',
                            mb: 6,
                            color: 'white',
                            fontWeight: 700
                        }}>
                            See It in Action
                        </Typography>
                        {isLoading ? (
                            <Box sx={{ textAlign: 'center', py: 8 }}>
                                <Typography variant="h6" sx={{ color: 'white' }}>
                                    Loading features...
                                </Typography>
                            </Box>
                        ) : (
                            <Grid container spacing={6}>
                                {/* Left Column - Features */}
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                        {features.slice(0, 3).map((feature, index) => (
                                            <Card
                                                key={feature.id}
                                                onMouseEnter={() => handleFeatureHover(feature)}
                                                onMouseLeave={handleFeatureLeave}
                                                sx={{
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease',
                                                    '&:hover': {
                                                        transform: 'translateY(-4px)',
                                                        boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                                                        borderColor: '#FF5722'
                                                    },
                                                    border: '2px solid transparent',
                                                    minHeight: 120
                                                }}
                                            >
                                                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                                                    <Box sx={{ mb: 1, display: 'flex', justifyContent: 'center' }}>
                                                        {getIconComponent(feature.icon)}
                                                    </Box>
                                                    <Typography variant="h6" sx={{
                                                        color: '#2c3e50',
                                                        fontWeight: 600,
                                                        mb: 1
                                                    }}>
                                                        {feature.title}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{
                                                        color: '#6c757d',
                                                        fontSize: '0.875rem'
                                                    }}>
                                                        {feature.description}
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </Box>
                                </Grid>

                                {/* Center Column - Media Display */}
                                <Grid item xs={12} md={4}>
                                    <Box sx={{
                                        backgroundColor: 'white',
                                        borderRadius: 2,
                                        border: '2px solid #e9ecef',
                                        minHeight: 300,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        overflow: 'hidden',
                                        position: 'relative'
                                    }}>
                                        {hoveredFeature ? (
                                            <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
                                                {hoveredFeature.mediaType === 'gif' ? (
                                                    <Box
                                                        component="img"
                                                        src={`/features/${hoveredFeature.media}`}
                                                        alt={hoveredFeature.title}
                                                        sx={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover'
                                                        }}
                                                    />
                                                ) : (
                                                    <Box
                                                        component="img"
                                                        src={`/features/${hoveredFeature.media}`}
                                                        alt={hoveredFeature.title}
                                                        sx={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover'
                                                        }}
                                                    />
                                                )}
                                                <Box sx={{
                                                    position: 'absolute',
                                                    bottom: 0,
                                                    left: 0,
                                                    right: 0,
                                                    background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                                                    p: 2
                                                }}>
                                                    <Typography variant="h6" sx={{
                                                        color: 'white',
                                                        fontWeight: 600,
                                                        textAlign: 'center'
                                                    }}>
                                                        {hoveredFeature.title}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ) : (
                                            <Box sx={{ textAlign: 'center', p: 4 }}>
                                                <Typography variant="h6" sx={{
                                                    color: '#6c757d',
                                                    mb: 2
                                                }}>
                                                    Hover over a feature
                                                </Typography>
                                                <Typography variant="body2" sx={{
                                                    color: '#9ca3af'
                                                }}>
                                                    to see it in action
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Grid>

                                {/* Right Column - Features */}
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                        {features.slice(3, 8).map((feature, index) => (
                                            <Card
                                                key={feature.id}
                                                onMouseEnter={() => handleFeatureHover(feature)}
                                                onMouseLeave={handleFeatureLeave}
                                                sx={{
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease',
                                                    '&:hover': {
                                                        transform: 'translateY(-4px)',
                                                        boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                                                        borderColor: '#FF5722'
                                                    },
                                                    border: '2px solid transparent',
                                                    minHeight: 120
                                                }}
                                            >
                                                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                                                    <Box sx={{ mb: 1, display: 'flex', justifyContent: 'center' }}>
                                                        {getIconComponent(feature.icon)}
                                                    </Box>
                                                    <Typography variant="h6" sx={{
                                                        color: '#2c3e50',
                                                        fontWeight: 600,
                                                        mb: 1
                                                    }}>
                                                        {feature.title}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{
                                                        color: '#6c757d',
                                                        fontSize: '0.875rem'
                                                    }}>
                                                        {feature.description}
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </Box>
                                </Grid>
                            </Grid>
                        )}
                    </Container>
                </Box>
            </Box>

            <Footer />
        </Box>
    );
}

export default DesktopHomeContent;
